import {
  type BannedLiquidPatternMatch,
  findBannedLiquidPattern,
} from "@hightouch/lib/templating/security";
import { transformV9ToV10 } from "@hightouch/lib/templating/v9-to-v10-transformer";

import { captureMessage } from "src/lib/monitoring";
import { v10Engine } from "src/utils/liquid-v10-engine";

// Deliberately vague: the pattern goes to telemetry, not to the user.
export const SECURITY_ERROR_MESSAGE =
  "This template was rejected. Please contact support for more information.";

const LIQUID_SYNTAX_RE = /\{\{|\{%/;

/** Runtime denylist over every Liquid string; template nodes are also v10-parsed. */
export function validateSyncConfigTemplates(
  config: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  const walk = (
    value: unknown,
    path: string,
    parent?: Record<string, unknown>,
  ) => {
    if (typeof value === "string") {
      if (!LIQUID_SYNTAX_RE.test(value)) return;
      const fieldName = typeof parent?.to === "string" ? parent.to : path;
      const banned = findBannedLiquidPattern(value);
      if (banned) {
        reportLiquidSecurityViolation(banned, fieldName, value);
        errors[fieldName] = SECURITY_ERROR_MESSAGE;
        return;
      }
      if (parent?.type === "template" && parent.template === value) {
        try {
          v10Engine.parse(transformV9ToV10(value));
        } catch (e) {
          errors[fieldName] = `v10 parse error: ${(e as Error).message}`;
        }
      }
      return;
    }
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) {
      value.forEach((item, i) => walk(item, `${path}[${i}]`, undefined));
      return;
    }
    const record = value as Record<string, unknown>;
    for (const [key, child] of Object.entries(record)) {
      walk(child, `${path}.${key}`, record);
    }
  };

  walk(config, "config");
  return errors;
}

/** Telemetry only; the server-side save gate enforces. */
function reportLiquidSecurityViolation(
  match: BannedLiquidPatternMatch,
  fieldName: string,
  template: string,
) {
  captureMessage("[SECURITY] Liquid banned pattern detected at save", {
    level: "fatal",
    tags: {
      security_event: "liquid_rce_attempt",
      liquid_pattern: match.name,
    },
    extra: {
      fieldName,
      snippet: match.snippet,
      templateLength: template.length,
    },
  });
}
